/**
 * Shared Sentry configuration for both client and server
 */
export const SENTRY_DSN =
	'https://301f36138b8fb5eadb575346186d3219@o968153.ingest.us.sentry.io/4507946504421376';

export const IGNORE_ERRORS = [
	/The email or password you have entered is incorrect/i,
	/Sorry, an unknown error occurred\. Please try again later\./i,
];

/**
 * Shared beforeSend hook for filtering events
 * @param {any} event - Sentry event
 * @returns {any|null} - Event to send or null to drop
 */
export function beforeSendHandler(event) {
	if (event.exception && Array.isArray(event.exception.values)) {
		const hasExplicitlyFalseFatal = event.exception.values.some(
			/**@param {{ value: string, fatal: boolean }} ex - Sentry exception value
			 * @returns {boolean} - True if the exception has a fatal property and it is false
			 */
			(ex) => 'fatal' in ex && ex.fatal === false
		);

		if (hasExplicitlyFalseFatal) {
			return null;
		}

		const hasNetworkError = event.exception.values.some(
			/**@param {{ value: string }} ex - Sentry exception value
			 * @returns {boolean} - True if the exception has a network error
			 */
			(ex) => {
				const message = ex.value || '';
				return (
					/fetch-failed: network-failed-to-fetch/i.test(message) ||
					/Failed to fetch/i.test(message) ||
					/load failed/i.test(message)
				);
			}
		);

		if (hasNetworkError) {
			event.fingerprint = ['network-error', 'fetch-failed', 'ignore-network-error'];

			const errorData = event.extra || {};
			if (errorData.offline === true) {
				event.tags = { ...event.tags, network_status: 'offline' };
			}
		}
	}

	if (
		event.fingerprint &&
		event.fingerprint.some(
			/**@param {string} f - Sentry fingerprint value
			 * @returns {boolean} - True if the fingerprint value includes 'ignore'
			 */
			(f) => f.includes('ignore')
		)
	) {
		return null;
	}

	return event;
}

/**
 * Get base Sentry configuration (common to client and server)
 * @param {object} options - Configuration options
 * @param {string} options.environment - Environment name
 * @param {string} options.release - Release name
 * @param {number} [options.tracesSampleRate] - Traces sample rate (optional, defaults to 0.25 for prod, 1 otherwise)
 * @returns {object} - Sentry init configuration
 */
export function getSentryConfig({ environment, release, tracesSampleRate }) {
	const defaultSampleRate = environment === 'production' ? 0.1 : 1;

	return {
		dsn: SENTRY_DSN,
		tracesSampleRate: tracesSampleRate ?? defaultSampleRate,
		environment,
		release,
		normalizeDepth: 6,
		attachStacktrace: true,
		ignoreErrors: IGNORE_ERRORS,
		beforeSend: beforeSendHandler,
	};
}
